import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, XCircle, ChevronLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get("/auth/reset-password/validate", { params: { token } })
      .then(({ data }) => {
        if (data.valid) { setState("valid"); setEmail(data.email || ""); }
        else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("6 caractères minimum"); return; }
    if (pwd !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      setState("done");
      toast.success("Mot de passe réinitialisé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-cream flex items-center justify-center px-6 relative">
      <Link to="/" data-testid="back-to-home" className="absolute top-6 left-6 flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-cream/50 hover:text-cream transition-colors">
        <ChevronLeft className="h-4 w-4" /> Accueil
      </Link>
      <div className="w-full max-w-md">
        <Link to="/" aria-label="Déstockage Luminaires — accueil" className="flex justify-center"><Logo to={null} size="lg" variant="dark" testId="reset-logo" /></Link>
        <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40 mt-1 text-center mb-10">Réinitialisation du mot de passe</p>

        {state === "checking" && (
          <p className="text-center text-cream/60 text-sm">Vérification du lien…</p>
        )}

        {state === "invalid" && (
          <div data-testid="reset-invalid" className="text-center space-y-4">
            <XCircle className="h-12 w-12 text-destructive mx-auto" strokeWidth={1.2} />
            <p className="text-cream/80">Ce lien est invalide ou a expiré.</p>
            <Link to="/connexion" className="inline-block bg-gold text-ink px-6 py-3 text-xs uppercase tracking-[0.15em]">Retour à la connexion</Link>
          </div>
        )}

        {state === "done" && (
          <div data-testid="reset-done" className="text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-gold mx-auto" strokeWidth={1.2} />
            <p className="text-cream/80">Votre mot de passe a été mis à jour.</p>
            <button data-testid="go-login" onClick={() => navigate("/connexion")} className="inline-block bg-gold text-ink px-6 py-3 text-xs uppercase tracking-[0.15em]">Se connecter</button>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit} data-testid="reset-form" className="space-y-5">
            {email && <p className="text-center text-cream/50 text-sm mb-2">Compte : <span className="text-cream">{email}</span></p>}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-cream/50">Nouveau mot de passe</label>
              <div className="relative mt-2">
                <Lock className="h-4 w-4 text-cream/30 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="reset-password" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={8} className="w-full h-12 pl-9 rounded-sm bg-white/5 border border-white/10 text-cream placeholder:text-cream/30 focus:outline-none focus:border-gold" placeholder="8 car. min · 1 lettre + 1 chiffre" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-cream/50">Confirmer le mot de passe</label>
              <div className="relative mt-2">
                <Lock className="h-4 w-4 text-cream/30 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="w-full h-12 pl-9 rounded-sm bg-white/5 border border-white/10 text-cream placeholder:text-cream/30 focus:outline-none focus:border-gold" placeholder="••••••••" />
              </div>
            </div>
            <button data-testid="reset-submit" type="submit" disabled={loading} className="w-full bg-gold text-ink py-4 uppercase tracking-[0.15em] text-xs hover:bg-cream transition-colors disabled:opacity-60">
              {loading ? "Enregistrement…" : "Réinitialiser le mot de passe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
